import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — GST return filing  (Phase 4.4)
//
// Distinct from the older GSTR-1 *summary* report: this screen prepares an
// actual return — section classification, blocking validation, and the GSTN
// offline-utility JSON.
//
// Two things it refuses to hide, because both silently produce a wrong filing:
//   · invoices that fail validation (an invalid GSTIN gets the whole return
//     rejected at upload, so export is blocked until they are fixed)
//   · invoices attributable to no registration (they would be missing from
//     every return)
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { FileJson, FileSpreadsheet, AlertTriangle, AlertCircle, CheckCircle2, Receipt } from 'lucide-react';

interface Registration {
  gstin: string;
  legalName: string;
  stateCode: string;
  isPrimary: boolean;
}

const money = (v: number | undefined | null) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function currentPeriod() {
  // Local parts, not toISOString — the latter names the previous month for the
  // first few hours of the 1st in IST.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function GstFilingPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [gstin, setGstin] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['gstr1', period, gstin],
    queryFn: async () => {
      const p = new URLSearchParams({ period });
      if (gstin) p.append('gstin', gstin);
      const { data } = await api.get(`/api/gstr/gstr1?${p.toString()}`);
      return data;
    },
  });

  const registrations: Registration[] = data?.availableRegistrations || [];
  const excluded = data?.excluded || [];
  const unattributed = data?.unattributed || [];
  const canExport = Boolean(data?.canExport) && !unattributed.length;

  const doExport = async (format: 'json' | 'csv') => {
    setExportError(null);
    try {
      const p = new URLSearchParams({ period, format });
      if (gstin) p.append('gstin', gstin);
      const res = await api.get(`/api/gstr/gstr1/export?${p.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `gstr1-${data?.registration?.gstin || 'return'}-${period}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // The server refuses with a reason (validation or unattributed invoices).
      // Surfaced verbatim rather than a generic failure message.
      let msg = 'Export failed.';
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try { msg = JSON.parse(await blob.text()).error || msg; } catch { /* keep default */ }
      } else if (blob?.error) {
        msg = blob.error;
      }
      setExportError(msg);
    }
  };

  const sections: Array<{ key: string; label: string; rows: any[] }> = [
    { key: 'b2b', label: 'B2B — registered customers', rows: data?.b2b || [] },
    { key: 'b2cl', label: 'B2CL — large inter-state, unregistered', rows: data?.b2cl || [] },
    { key: 'b2cs', label: 'B2CS — small, consolidated', rows: data?.b2cs || [] },
    { key: 'cdnr', label: 'CDNR — credit / debit notes', rows: data?.cdnr || [] },
  ];

  return (
    <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
      <PageHeader icon={Receipt} eyebrow="Finance" title="GST Return Filing" description={<>Prepares GSTR-1 for one registration. Each registration files separately.</>} actions={<><div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] font-semibold block mb-1">Period</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 rounded-lg border border-outline bg-transparent text-sm"
            />
          </div>
          {registrations.length > 1 && (
            <div>
              <label className="text-[11px] font-semibold block mb-1">Registration</label>
              <select
                value={gstin}
                onChange={(e) => setGstin(e.target.value)}
                className="px-3 py-2 rounded-lg border border-outline bg-transparent text-sm"
              >
                {registrations.map((r) => (
                  <option key={r.gstin} value={r.gstin}>
                    {r.gstin} — {r.legalName}{r.isPrimary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={() => doExport('json')}
            disabled={!canExport}
            className="px-3 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
            title={canExport ? 'Download the GSTN upload JSON' : 'Resolve the issues below first'}
          >
            <FileJson className="w-4 h-4" /> GSTN JSON
          </button>
          <button
            onClick={() => doExport('csv')}
            disabled={!canExport}
            className="px-3 py-2 rounded-lg border border-outline text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
          >
            <FileSpreadsheet className="w-4 h-4" /> CSV
          </button>
        </div></>} />

      {isLoading && <Skeleton className="h-40 w-full" />}
      {isError && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-sm text-rose-800 dark:text-rose-300">
          Could not load the return.
        </div>
      )}

      {data && !data.registration && (
        <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200 flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">No GST registration configured.</p>
            <p className="text-xs mt-1">
              Add one under Settings → GST Registrations. Nothing can be filed until then.
            </p>
          </div>
        </div>
      )}

      {data?.registration && (
        <div className="p-4 rounded-xl bg-surface border border-outline text-sm flex flex-wrap gap-x-8 gap-y-2">
          <span><span className="text-on-surface-variant">Filing as </span>
            <span className="font-mono font-bold">{data.registration.gstin}</span></span>
          <span><span className="text-on-surface-variant">Seller state </span>
            <span className="font-semibold">{data.sellerStateCode || '—'}</span></span>
          <span><span className="text-on-surface-variant">Taxable </span>
            <span className="font-semibold">{money(data.totals?.taxableValue)}</span></span>
          <span><span className="text-on-surface-variant">Tax </span>
            <span className="font-semibold">
              {money((data.totals?.igst || 0) + (data.totals?.cgst || 0) + (data.totals?.sgst || 0))}
            </span></span>
        </div>
      )}

      {exportError && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-sm text-rose-800 dark:text-rose-300">
          {exportError}
        </div>
      )}

      {/* Blocking: these invoices would get the whole return rejected. */}
      {excluded.length > 0 && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800">
          <p className="font-semibold text-sm text-rose-800 dark:text-rose-300 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {excluded.length} invoice(s) must be corrected before this return can be filed
          </p>
          <ul className="mt-2 text-xs space-y-1 text-rose-700 dark:text-rose-300 max-h-48 overflow-y-auto">
            {excluded.map((e: any, i: number) => (
              <li key={i}>
                <span className="font-mono">{e.invoiceNumber || '(no number)'}</span> — {(e.issues || []).join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Blocking: filing without these would under-report the period. */}
      {unattributed.length > 0 && (
        <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
          <p className="font-semibold text-sm text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {data.unattributedCount} invoice(s) belong to no GST registration
          </p>
          <p className="text-xs mt-1 text-amber-800 dark:text-amber-300">
            Map their branch under Settings → GST Registrations. Until then they are missing from
            every return, not just this one.
          </p>
          <ul className="mt-2 text-xs space-y-1 text-amber-800 dark:text-amber-300 max-h-40 overflow-y-auto">
            {unattributed.map((u: any, i: number) => (
              <li key={i}>
                <span className="font-mono">{u.invoiceNumber || '(no number)'}</span> — {money(u.invoiceValue)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data?.registration && canExport && (
        <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Ready to export. Validate the file in the offline utility before filing — the GSTN schema
          is version-pinned per portal release.
        </div>
      )}

      {data && sections.map((s) => (
        <div key={s.key} className="rounded-xl border border-outline overflow-hidden">
          <div className="px-4 py-3 bg-surface-variant/40 flex items-center justify-between">
            <h2 className="text-sm font-bold">{s.label}</h2>
            <span className="text-xs text-on-surface-variant">{s.rows.length} row(s)</span>
          </div>
          {s.rows.length === 0 ? (
            <p className="px-4 py-3 text-xs text-on-surface-variant">Nothing in this section.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-surface-variant/20 text-on-surface-variant">
                  <tr>
                    <th className="text-left px-4 py-2">GSTIN</th>
                    <th className="text-left px-4 py-2">Invoice</th>
                    <th className="text-left px-4 py-2">Place of supply</th>
                    <th className="text-right px-4 py-2">Taxable</th>
                    <th className="text-right px-4 py-2">IGST</th>
                    <th className="text-right px-4 py-2">CGST</th>
                    <th className="text-right px-4 py-2">SGST</th>
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((r: any, i: number) => (
                    <tr key={i} className="border-t border-outline/50">
                      <td className="px-4 py-2 font-mono">{r.gstin || '—'}</td>
                      <td className="px-4 py-2">
                        {r.invoiceNumber || (r.invoices ? `${r.invoices} invoice(s)` : '—')}
                      </td>
                      <td className="px-4 py-2">{r.placeOfSupply || '—'}</td>
                      <td className="px-4 py-2 text-right">{money(r.taxableValue)}</td>
                      <td className="px-4 py-2 text-right">{money(r.igst)}</td>
                      <td className="px-4 py-2 text-right">{money(r.cgst)}</td>
                      <td className="px-4 py-2 text-right">{money(r.sgst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default GstFilingPage;
